import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Alert,
  AlertDescription,
  AlertTitle,
} from "@vaiu/ui/alert";
import { Button } from "@vaiu/ui/button";
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from "@vaiu/ui/card";
import { Badge } from "@vaiu/ui/badge";
import { 
  UserPlus, 
  Clock, 
  Shield, 
  Zap, 
  Gift,
  X,
  ChevronRight,
  Lock,
  Unlock
} from 'lucide-react';
import { useAuth } from '@/context/AuthContext';

interface GuestUpgradePromptProps {
  variant?: 'inline' | 'modal' | 'banner';
  onDismiss?: () => void;
  showSessionExpiry?: boolean;
}

const GuestUpgradePrompt: React.FC<GuestUpgradePromptProps> = ({ 
  variant = 'banner', 
  onDismiss,
  showSessionExpiry = true 
}) => {
  const navigate = useNavigate();
  const { user, isGuest } = useAuth();
  const [isDismissed, setIsDismissed] = useState(false);

  // Don't show if not a guest or if dismissed
  if (!isGuest || isDismissed) {
    return null;
  }

  const handleUpgrade = () => {
    navigate('/signup', { state: { fromGuest: true, guestUsername: user?.username } });
  };

  const handleDismiss = () => {
    setIsDismissed(true);
    onDismiss?.();
  };

  // Calculate time remaining for guest session
  const getSessionTimeRemaining = () => {
    if (!user?.sessionExpiry) return null;
    
    const now = new Date();
    const expiry = new Date(user.sessionExpiry);
    const diff = expiry.getTime() - now.getTime();
    
    if (diff <= 0) return 'Session expired';
    
    const hours = Math.floor(diff / (1000 * 60 * 60));
    const minutes = Math.floor((diff % (1000 * 60 * 60)) / (1000 * 60));
    
    if (hours > 24) {
      const days = Math.floor(hours / 24);
      return `${days} day${days > 1 ? 's' : ''} remaining`;
    }
    
    return `${hours}h ${minutes}m remaining`;
  };

  const benefits = [
    { icon: Shield, text: "Save your account permanently", color: "text-green-600" },
    { icon: Unlock, text: "Log back in anytime", color: "text-blue-600" },
    { icon: Gift, text: "Keep your transaction history", color: "text-purple-600" },
    { icon: Zap, text: "Access from any device", color: "text-yellow-600" }
  ];

  if (variant === 'banner') {
    return (
      <Alert className="mb-4 border-blue-200 bg-blue-50/50">
        <UserPlus className="h-4 w-4 text-blue-600" />
        <AlertTitle className="flex items-center justify-between">
          <span>You're using Vaiu as a guest</span>
          {showSessionExpiry && user?.sessionExpiry && (
            <Badge variant="outline" className="ml-2 text-xs">
              <Clock className="w-3 h-3 mr-1" />
              {getSessionTimeRemaining()}
            </Badge>
          )}
        </AlertTitle>
        <AlertDescription className="mt-2">
          <div className="flex flex-col gap-3">
            <span className="text-sm">
              Create a free account to save your progress and log back in later.
            </span>
            <div className="flex items-center gap-2">
              <Button 
                size="sm" 
                variant="default"
                onClick={handleUpgrade}
                className="whitespace-nowrap"
              >
                Sign Up Free
                <ChevronRight className="w-3 h-3 ml-1" />
              </Button>
              <Button 
                size="sm" 
                variant="ghost"
                onClick={handleDismiss}
                className="text-gray-500 hover:text-gray-700"
              >
                <X className="w-4 h-4" />
              </Button>
            </div>
          </div>
        </AlertDescription>
      </Alert>
    );
  }

  if (variant === 'inline') {
    return (
      <Card className="border-blue-200 bg-gradient-to-br from-blue-50 to-indigo-50">
        <CardHeader className="pb-4">
          <div className="flex items-start justify-between">
            <div>
              <CardTitle className="text-lg flex items-center space-x-2">
                <UserPlus className="w-5 h-5 text-blue-600" />
                <span>Upgrade Your Guest Account</span>
              </CardTitle>
              <CardDescription className="mt-1">
                You're currently using Vaiu as <strong>{user?.username}</strong>
              </CardDescription>
            </div>
            {showSessionExpiry && user?.sessionExpiry && (
              <Badge variant="secondary" className="text-xs">
                <Clock className="w-3 h-3 mr-1" />
                {getSessionTimeRemaining()}
              </Badge>
            )}
          </div>
        </CardHeader>
        <CardContent className="space-y-4">
          <div className="grid grid-cols-2 gap-3">
            {benefits.map((benefit, index) => (
              <div key={index} className="flex items-center space-x-2">
                <benefit.icon className={`w-4 h-4 ${benefit.color}`} />
                <span className="text-sm">{benefit.text}</span>
              </div>
            ))}
          </div>
          
          <div className="bg-white/80 rounded-lg p-3 border border-blue-100">
            <p className="text-sm text-gray-600 mb-2">
              <Lock className="w-3 h-3 inline mr-1" />
              As a guest, you have one limitation:
            </p>
            <ul className="text-xs text-gray-500 space-y-1 ml-4">
              <li>• You cannot log back in after logging out</li>
              <li>• Your session expires in 48 hours</li>
              <li>• Your data will be lost after session expires</li>
            </ul>
            <p className="text-xs text-green-600 mt-2">
              ✓ You can use all features while logged in!
            </p>
          </div>

          <div className="flex space-x-3">
            <Button 
              className="flex-1"
              onClick={handleUpgrade}
            >
              Create Free Account
              <ChevronRight className="w-4 h-4 ml-1" />
            </Button>
            <Button 
              variant="outline"
              className="flex-1"
              onClick={handleDismiss}
            >
              Continue as Guest
            </Button>
          </div>
        </CardContent>
      </Card>
    );
  }

  // Modal variant (can be implemented later if needed)
  return null;
};

export default GuestUpgradePrompt;
